// frontend/src/utils/calendarExport.js

/**
 * Formats a Date object or date string to iCalendar date-time string in UTC (YYYYMMDDTHHmmssZ)
 * or local time with Europe/Berlin
 */
const formatIcsDate = (dateStr, timeStr = '09:00') => {
  if (!dateStr) return '';
  const d = new Date(dateStr);
  if (isNaN(d.getTime())) return '';

  const yyyy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');

  const [hours = '09', mins = '00'] = (timeStr || '09:00').split(':');
  const hh = String(hours).padStart(2, '0');
  const min = String(mins).padStart(2, '0');

  return `${yyyy}${mm}${dd}T${hh}${min}00`;
};

/**
 * Generates an .ics file content for an event
 */
export const generateIcsContent = (event) => {
  if (!event || !event.date) return '';

  const startTime = event.startTime || '09:00';
  let endTime = event.endTime;
  if (!endTime) {
    const [h, m] = startTime.split(':');
    const endH = (parseInt(h, 10) + 3) % 24;
    endTime = `${String(endH).padStart(2, '0')}:${m || '00'}`;
  }

  const dtStart = formatIcsDate(event.date, startTime);
  const dtEnd = formatIcsDate(event.date, endTime);
  const now = new Date().toISOString().replace(/[-:]/g, '').split('.')[0] + 'Z';
  const uid = `${event._id || event.slug || Date.now()}@pfadfinder-wutoeschingen.org`;

  const cleanDescription = (event.description || '')
    .replace(/\r\n/g, '\n')
    .replace(/\n/g, '\\n')
    .replace(/,/g, '\\,')
    .replace(/;/g, '\\;');

  const cleanTitle = (event.title || 'Pfadfinder Event')
    .replace(/,/g, '\\,')
    .replace(/;/g, '\\;');

  const cleanLocation = (event.location || 'Wutöschingen')
    .replace(/,/g, '\\,')
    .replace(/;/g, '\\;');

  const siteUrl = typeof window !== 'undefined' ? window.location.origin : 'https://pfadfinder-wutoeschingen.org';
  const eventUrl = event.slug ? `${siteUrl}/events/${event.slug}` : siteUrl;

  return [
    'BEGIN:VCALENDAR',
    'VERSION:2.0',
    'PRODID:-//Pfadfinder Wutoeschingen//Event Calendar//DE',
    'CALSCALE:GREGORIAN',
    'METHOD:PUBLISH',
    'BEGIN:VEVENT',
    `UID:${uid}`,
    `DTSTAMP:${now}`,
    `DTSTART;TZID=Europe/Berlin:${dtStart}`,
    `DTEND;TZID=Europe/Berlin:${dtEnd}`,
    `SUMMARY:${cleanTitle}`,
    `DESCRIPTION:${cleanDescription}\\n\\nInfos: ${eventUrl}`,
    `LOCATION:${cleanLocation}`,
    `URL:${eventUrl}`,
    'STATUS:CONFIRMED',
    'END:VEVENT',
    'END:VCALENDAR'
  ].join('\r\n');
};

/**
 * Triggers a browser download for the .ics calendar file
 */
export const downloadIcsFile = (event) => {
  const icsContent = generateIcsContent(event);
  if (!icsContent) return;

  const blob = new Blob([icsContent], { type: 'text/calendar;charset=utf-8' });
  const filename = `${(event.slug || event.title || 'event').toLowerCase().replace(/[^a-z0-9]/g, '-')}.ics`;

  const link = document.createElement('a');
  link.href = window.URL.createObjectURL(blob);
  link.setAttribute('download', filename);
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  window.URL.revokeObjectURL(link.href);
};

/**
 * Generates a direct Google Calendar template URL
 */
export const getGoogleCalendarUrl = (event) => {
  if (!event || !event.date) return '#';

  const startTime = event.startTime || '09:00';
  let endTime = event.endTime;
  if (!endTime) {
    const [h, m] = startTime.split(':');
    const endH = (parseInt(h, 10) + 3) % 24;
    endTime = `${String(endH).padStart(2, '0')}:${m || '00'}`;
  }

  const dtStart = formatIcsDate(event.date, startTime);
  const dtEnd = formatIcsDate(event.date, endTime);

  const siteUrl = typeof window !== 'undefined' ? window.location.origin : 'https://pfadfinder-wutoeschingen.org';
  const eventUrl = event.slug ? `${siteUrl}/events/${event.slug}` : siteUrl;
  const details = `${event.description || ''}\n\nWeitere Infos & Anmeldung:\n${eventUrl}`;

  const params = new URLSearchParams({
    action: 'TEMPLATE',
    text: event.title || 'Pfadfinder Event',
    dates: `${dtStart}/${dtEnd}`,
    details: details,
    location: event.location || 'Wutöschingen',
    ctz: 'Europe/Berlin'
  });

  return `https://calendar.google.com/calendar/render?${params.toString()}`;
};
